import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { CalendarPlus, CheckCircle2, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ATTENDANCE_STATUSES } from "../../lib/types";
import { todayStr, formatDatePretty } from "../../lib/progress";
import { storage } from "../../lib/storage";
import { STATUS_STYLES } from "./constants";
import { MonthStat } from "./MonthStat";

const DEFAULT_STATUS = "Present";

/**
 * QuickAttendancePanel — the daily-use logging card on the Student Detail
 * page. Phase D additions:
 *   • Status starts as last-picked (persisted across students).
 *   • Exposes imperative handle: { setStatus, save, setToday, undoLast }.
 *   • Save success toast carries an "Undo" action.
 */
export const QuickAttendancePanel = forwardRef(function QuickAttendancePanel(
    { student, saveAttendance, removeAttendance, todayLogged, monthStats, onSaved },
    ref,
) {
    const initialStatus = storage.getLastStatus() || DEFAULT_STATUS;
    const [date, setDate] = useState(todayStr());
    const [status, _setStatus] = useState(
        ATTENDANCE_STATUSES.includes(initialStatus) ? initialStatus : DEFAULT_STATUS,
    );
    const [remark, setRemark] = useState("");

    const setStatus = (s) => {
        if (!ATTENDANCE_STATUSES.includes(s)) return;
        _setStatus(s);
        storage.setLastStatus(s);
    };

    const setToday = () => setDate(todayStr());

    // Reset the per-student remark when the student id changes (otherwise a
    // note typed for student A would carry to B).
    useEffect(() => {
        setRemark("");
        setDate(todayStr());
    }, [student.id]);

    const performSave = (overrideStatus) => {
        if (!date) {
            toast.error("Pick a date first");
            return null;
        }
        const effective = overrideStatus || status;
        const saved = saveAttendance({
            studentId: student.id,
            date,
            status: effective,
            remark: remark.trim(),
        });
        if (saved) {
            toast.success(`Attendance saved for ${formatDatePretty(date)}`, {
                action: {
                    label: "Undo",
                    onClick: () => {
                        removeAttendance?.({ studentId: student.id, date });
                        toast.success("Reverted");
                    },
                },
            });
            setRemark("");
            if (saved.id) onSaved?.(saved);
        }
        return saved;
    };

    const submit = (e) => {
        e.preventDefault();
        performSave();
    };

    const quickMarkTodayPresent = () => {
        const saved = saveAttendance({
            studentId: student.id,
            date: todayStr(),
            status: "Present",
            remark: "",
        });
        if (!saved) return;
        toast.success(`${student.name} marked Present for today`, {
            action: {
                label: "Undo",
                onClick: () => {
                    removeAttendance?.({
                        studentId: student.id,
                        date: todayStr(),
                    });
                    toast.success("Reverted");
                },
            },
        });
        if (saved.id) onSaved?.(saved);
    };

    useImperativeHandle(
        ref,
        () => ({
            setStatus,
            save: () => performSave(),
            setToday,
            quickMarkTodayPresent,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [student.id, date, status, remark, todayLogged],
    );

    return (
        <div className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8">
            <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-action-soft text-[#3D6385] flex items-center justify-center">
                        <CalendarPlus size={20} strokeWidth={2.5} />
                    </div>
                    <h2 className="font-display font-bold text-2xl uppercase tracking-tight">
                        Log Attendance
                    </h2>
                </div>
                {todayLogged ? (
                    <div
                        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-success bg-success-soft border border-success-soft px-3 py-2 rounded-xl"
                        data-testid="today-already-logged"
                    >
                        <CheckCircle2 size={14} strokeWidth={2.8} />
                        Today logged · {todayLogged.status}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={quickMarkTodayPresent}
                        data-testid="quick-mark-today-present"
                        className="focus-ring inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white bg-success hover:bg-success active:bg-success/85 px-3 py-2 rounded-xl shadow-sm transition-colors"
                    >
                        <Zap size={14} strokeWidth={2.8} />
                        Mark Today Present
                    </button>
                )}
            </div>

            <div className="grid grid-cols-4 gap-2 mb-6" data-testid="month-stats">
                <MonthStat
                    label="Present"
                    value={monthStats.present}
                    tone="emerald"
                    testId="month-stat-present"
                />
                <MonthStat
                    label="Doubles"
                    value={monthStats.doubles}
                    tone="sky"
                    testId="month-stat-doubles"
                />
                <MonthStat
                    label="Absent"
                    value={monthStats.absent}
                    tone="rose"
                    testId="month-stat-absent"
                />
                <MonthStat
                    label="Excused"
                    value={monthStats.excused}
                    tone="amber"
                    testId="month-stat-excused"
                />
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="attendance-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Date
                            </Label>
                            <button
                                type="button"
                                onClick={setToday}
                                disabled={date === todayStr()}
                                data-testid="attendance-date-today"
                                className="focus-ring text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border border-mist text-slate-600 hover:bg-mist hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Today
                            </button>
                        </div>
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="h-12 rounded-xl bg-mist/50 border-mist"
                            data-testid="attendance-date"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Status
                    </Label>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {ATTENDANCE_STATUSES.map((s) => {
                            const styles = STATUS_STYLES[s];
                            const active = status === s;
                            return (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setStatus(s)}
                                    aria-pressed={active}
                                    data-testid={`status-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={`focus-ring h-14 rounded-xl border-2 text-sm font-bold transition-all ${
                                        active ? styles.active : styles.idle
                                    }`}
                                >
                                    {s}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Remark (optional)
                    </Label>
                    <Textarea
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                        placeholder="Any notes for this session…"
                        className="rounded-xl bg-mist/50 border-mist min-h-[80px]"
                        data-testid="attendance-remark"
                    />
                </div>

                <div className="flex justify-end">
                    <Button
                        type="submit"
                        className="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold h-12 px-6 shadow-md"
                        data-testid="save-attendance-btn"
                    >
                        Save Attendance
                    </Button>
                </div>
            </form>
        </div>
    );
});
